import { createContext, memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ConfigProvider, Table } from 'antd';
import { createStyles } from 'antd-style';

import { DndContext, DragOverlay, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { getTablePreferences } from '../../helpers/table-configurations';

import { DEFAULT_PAGE_SIZE } from '../../constants/common';

import { resetUserTablePreferences, updateUserTablePreferences } from '../../redux/actions/user';
import { upsertTablePageSize } from '../../redux/actions/userSlice';
import { setSelectedFilters } from '../../redux/reducers/filters';
import { setTableData } from '../../redux/reducers/table';

import {
  ACTIONS,
  FONT_SIZE,
  PAGE_SIZE_OPTIONS,
  ROW_SELECTION_TYPE,
  TABLE_SIZE
} from '../../components/AppTable/constants';

import './styles.scss';

import AppTableControls from './AppTableControls';
import EditableRow from './EditableRow';
import TableBodyCell from './TableBodyCell';
import TableHeaderCell from './TableHeaderCell';
import getColumnFilterProps from './getColumnFilterProps';
import getColumnSearchProps from './getColumnSearchProps';
import usePreparedColumns from './usePreparedColumns';

dayjs.extend(customParseFormat);

const useStyle = createStyles(({ css, token }) => {
  const { antCls } = token;
  return {
    customTable: css`
      ${antCls}-table {
        ${antCls}-table-container {
          ${antCls}-table-body,
          ${antCls}-table-content {
            scrollbar-width: thin;
            scrollbar-color: #eaeaea transparent;
            scrollbar-gutter: stable;
          }
        }
      }
    `
  };
});

export const DragIndexContext = createContext({ active: -1, over: -1 });
export const EditableContext = createContext(null);

const AppTable = ({
  actions,
  baseColumns,
  description,
  controlsVisible = true,
  dataSource,
  expandable,
  filterable,
  headerClass,
  loading,
  pageSize = DEFAULT_PAGE_SIZE,
  rowClassName,
  rowSelectionType = ROW_SELECTION_TYPE.CHECKBOX,
  rowsSelectable = true,
  searchPlaceholder,
  searchable,
  selectedRows,
  settings,
  tableConfigKey,
  tableMaxHeight = '65vh',
  title,
  titleVisible = true,
  handleFilters,
  handleOnChange,
  handleOnSelectRecords,
  handleSearchChange,
  updateTableData,
  updatedDataSource // used to trigger the rebuild the roes on cancellation. A separate prop is defined to omit rebuild on every dataSource change
}) => {
  const dispatch = useDispatch();

  const { tableSettings, tablePreferences } = useSelector(state => state.user);
  const { tableSettings: userTableSettings } = useSelector(state => state.userSlice);

  const [dragIndex, setDragIndex] = useState({ active: -1, over: -1 });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [tableDataSource, setDataSource] = useState(dataSource ?? []);
  const [tableVersion, setTableVersion] = useState(0); // required to rebuild the table view when datasource is changed

  useEffect(() => {
    setDataSource(dataSource?.length ? [...dataSource] : []);
  }, [dataSource]);

  useEffect(() => {
    if (updatedDataSource) {
      setDataSource([...updatedDataSource]);
      setTableVersion(prev => prev + 1);
    }
  }, [updatedDataSource]);

  const tableColumnPreferences = useMemo(
    () => getTablePreferences(tableConfigKey, tablePreferences),
    [tablePreferences, tableConfigKey]
  );

  const defaultPageSize =
    userTableSettings?.find(t => t.table_id === tableColumnPreferences?.id)?.page_size ?? pageSize;

  async function handleChangeTable(pagination, filters, sorter, extra) {
    if (extra.action === ACTIONS.PAGINATE && tableColumnPreferences?.id) {
      await dispatch(
        upsertTablePageSize({ page_size: pagination.pageSize, table_id: tableColumnPreferences?.id })
      ).unwrap();
    }

    if (!controlsVisible && extra.action === ACTIONS.FILTER) {
      await dispatch(setSelectedFilters(filters));
    }

    handleOnChange?.(pagination, filters, sorter, { currentDataSource: extra.currentDataSource });
  }

  function handleChange(updatedRecord) {
    updateTableData?.([updatedRecord]);
  }

  const handleSave = updatedRecord => {
    updateTableData?.([updatedRecord]);
  };

  const renderColumns = usePreparedColumns({
    baseColumns,
    tableColumnPreferences,
    getColumnFilterProps,
    getColumnSearchProps,
    handleChange,
    handleSave
  });

  useEffect(() => {
    dispatch(setSelectedFilters({}));
  }, [tableColumnPreferences?.id]);

  useEffect(() => {
    if (!controlsVisible && tableColumnPreferences) {
      const modifiedColumns = renderColumns?.map(
        ({ columnReferenceId, configColumnKey, hidden, id, key, order, title }) => ({
          columnReferenceId,
          configColumnKey,
          hidden,
          id,
          key,
          order,
          title
        })
      );
      dispatch(setTableData({ tableId: tableColumnPreferences.id, preparedColumns: modifiedColumns }));
    }
  }, [controlsVisible, dispatch, renderColumns, tableColumnPreferences?.id]);

  const [columns, setColumns] = useState(renderColumns);

  useEffect(() => {
    const updatedColumns = renderColumns;

    if (updatedColumns?.length) {
      setColumns(updatedColumns);
    }
  }, [dataSource, baseColumns, tableColumnPreferences]);

  const onSelectChange = newSelectedRowKeys => {
    setSelectedRowKeys(newSelectedRowKeys);
    handleOnSelectRecords(newSelectedRowKeys);
  };

  const rowSelection = {
    type: rowSelectionType,
    columnWidth: '40px',
    // fixed: true,
    selectedRowKeys: selectedRows || selectedRowKeys,
    onChange: onSelectChange,
    selections: [
      {
        key: 'all-page',
        text: 'Select all data on this page',
        onSelect: changeableRowKeys => {
          setSelectedRowKeys(changeableRowKeys);
          onSelectChange(changeableRowKeys);
        }
      },
      {
        key: 'all',
        text: 'Select all data',
        onSelect: () => {
          const data = [...tableDataSource];
          const newSelectedRowKeys = data.map(item => item.id);

          setSelectedRowKeys(newSelectedRowKeys);
          onSelectChange(newSelectedRowKeys);
        }
      },
      Table.SELECTION_NONE
    ]
  };

  const [size, setSize] = useState(TABLE_SIZE.SM);

  useEffect(() => {
    if (!controlsVisible) {
      setSize(tableSettings?.size);
    }
  }, [tableSettings?.size]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1
      }
    })
  );
  const { styles } = useStyle();

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setColumns(prevState => {
        const activeIndex = prevState.findIndex(i => i.id === active?.id);
        const overIndex = prevState.findIndex(i => i.id === over?.id);

        const movedColumns = arrayMove(prevState, activeIndex, overIndex);

        if (tableColumnPreferences?.id) {
          dispatch(updateUserTablePreferences(tableColumnPreferences.id, composeColumnsToSave(movedColumns)));
        }

        return movedColumns;
      });
    }

    setDragIndex({ active: -1, over: -1 });
  };

  const onDragOver = ({ active, over }) => {
    const activeIndex = columns.findIndex(i => i.id === active.id);
    const overIndex = columns.findIndex(i => i.id === over?.id);

    setDragIndex({
      active: active.id,
      over: over?.id,
      direction: overIndex > activeIndex ? 'right' : 'left'
    });
  };

  const composeColumnsToSave = updatedColumns =>
    updatedColumns.map(({ columnReferenceId, hidden }) => ({
      id: columnReferenceId,
      is_visible: !hidden
    }));

  function handleCheckedList(checkedList) {
    if (tableColumnPreferences) {
      dispatch(updateUserTablePreferences(tableColumnPreferences.id, composeColumnsToSave(checkedList)));
    }

    setColumns(checkedList);
  }

  const handleResetSettings = async () => {
    if (!tableColumnPreferences?.id) {
      return;
    }

    dispatch(resetUserTablePreferences(tableColumnPreferences?.id));
  };

  return (
    <>
      {controlsVisible && (
        <AppTableControls
          actions={actions}
          columns={columns}
          description={tableColumnPreferences ? tableColumnPreferences.description : description}
          filterable={filterable}
          headerClass={headerClass}
          loading={loading}
          searchPlaceholder={searchPlaceholder}
          searchable={searchable}
          settings={settings}
          tableId={tableColumnPreferences?.id}
          title={
            tableColumnPreferences?.title && titleVisible
              ? tableColumnPreferences.title
              : title && titleVisible
                ? title
                : ''
          }
          handleCheckedList={handleCheckedList}
          handleFilters={handleFilters}
          handleResetSettings={handleResetSettings}
          handleSearchChange={handleSearchChange}
          handleTableSize={size => setSize(size)}
        />
      )}

      <DndContext
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={closestCenter}>
        <DragIndexContext.Provider value={dragIndex}>
          <SortableContext items={columns.map(i => i.id)} strategy={horizontalListSortingStrategy}>
            <ConfigProvider
              theme={{
                token: {
                  fontSize: size === TABLE_SIZE.SM ? FONT_SIZE.SM : size === TABLE_SIZE.LG ? FONT_SIZE.LG : FONT_SIZE.MD
                },
                components: {
                  Select: {
                    optionFontSize:
                      size === TABLE_SIZE.SM ? FONT_SIZE.SM : size === TABLE_SIZE.LG ? FONT_SIZE.LG : FONT_SIZE.MD
                  }
                }
              }}>
              <Table
                expandable={expandable}
                rowSelection={rowsSelectable ? rowSelection : null}
                selections={false}
                columns={columns}
                dataSource={tableDataSource}
                components={{
                  header: { cell: TableHeaderCell },
                  body: { cell: TableBodyCell, row: EditableRow }
                }}
                rowKey="id"
                rowClassName={rowClassName}
                bordered
                loading={loading}
                className={`app-table ${styles.customTable}`}
                size={size}
                scroll={{ x: 200, y: tableMaxHeight, scrollToFirstRowOnChange: true }}
                showSorterTooltip={{ target: 'sorter-icon' }}
                pagination={{
                  defaultPageSize,
                  showTotal: (total, range) => `${range[1]} of ${total}`,
                  pageSizeOptions: PAGE_SIZE_OPTIONS
                }}
                onChange={handleChangeTable}
                key={tableVersion}
              />
            </ConfigProvider>
          </SortableContext>
        </DragIndexContext.Provider>

        <DragOverlay>
          <th style={{ backgroundColor: 'gray', padding: 16 }}>
            {columns[columns.findIndex(i => i.id === dragIndex.active)]?.title}
          </th>
        </DragOverlay>
      </DndContext>
    </>
  );
};

export default memo(AppTable);
